import { useCallback, useEffect, useRef, useState } from 'react';

/** 登録する Service Worker のパス（scope は '/'） */
const SERVICE_WORKER_URL = '/sw.js';

/** 開きっぱなしのタブでも新デプロイに気づけるよう、定期的に更新確認する間隔 */
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

/**
 * SKIP_WAITING 送信後に controllerchange を待つ上限。
 * 他タブが activate を握っている等で controllerchange が来ないと、
 * ［更新する］を押しても何も起きずリトライ手段も無いボタンになる。
 */
const SKIP_WAITING_TIMEOUT_MS = 5000;

export interface AppUpdateState {
  /** 新しいバージョンがインストール済みで、切り替えを待っている */
  isUpdateReady: boolean;
  /** ユーザーが更新を受け入れたときに呼ぶ。新 SW を確定させてリロードする */
  applyUpdate: () => void;
  /** 「あとで」。同じバージョンでは再通知しない */
  dismissUpdate: () => void;
}

/**
 * Service Worker を登録し、「新しいバージョンが待機している」状態を管理する。
 *
 * 【方針（CubecHF-Unified の更新トーストから引き継ぐ）】
 * - **自動リロードしない。** 動画アップロード中・コメント入力中に画面が飛ぶ事故を避ける。
 *   ユーザーが applyUpdate() を呼んだときだけ切り替える。
 * - 判定できない環境（dev / SW 非対応 / デモ）では何も出さない。
 *
 * 【enabled を引数で受け取る理由】
 * NODE_ENV をこの中で見て早期 return すると、テスト環境では常に無効になり
 * 本体のロジックが一切カバーされない。判定は shouldRegisterServiceWorker()
 * （src/utils/pwa.ts）に置き、ここには結果だけを渡す。
 */
export const useAppUpdate = (enabled: boolean): AppUpdateState => {
  const [isUpdateReady, setIsUpdateReady] = useState<boolean>(false);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  /** ユーザーが「更新する」を押したか。controllerchange でのリロード可否を決める */
  const updateAcceptedRef = useRef<boolean>(false);
  /** リロードを二重に走らせないためのラッチ */
  const reloadingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const container = navigator.serviceWorker;
    let disposed = false;
    let trackedRegistration: ServiceWorkerRegistration | null = null;

    const markUpdateReady = (): void => {
      if (disposed) return;
      setIsUpdateReady(true);
    };

    const watchInstalling = (worker: ServiceWorker): void => {
      const evaluate = (): void => {
        // controller がいる = 既存版が動いている上での「更新」。
        // 初回インストールでは controller が null なので通知しない。
        if (worker.state === 'installed' && container.controller) markUpdateReady();
      };
      worker.addEventListener('statechange', evaluate);
      // リスナー登録より前に installed へ到達しているケースを取りこぼさない
      // （state は登録時点の値を持っているので、その場で 1 回評価する）。
      evaluate();
    };

    const handleControllerChange = (): void => {
      // 初回インストールの clients.claim() でも controllerchange は飛ぶ。
      // 受入フラグを見ないと、初回訪問者が理由もなく再読み込みされる。
      if (!updateAcceptedRef.current) return;
      if (reloadingRef.current) return;
      reloadingRef.current = true;
      window.location.reload();
    };

    const requestUpdateCheck = (): void => {
      void registrationRef.current?.update().catch(() => undefined);
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') requestUpdateCheck();
    };

    const handleUpdateFound = (): void => {
      const installing = trackedRegistration?.installing;
      if (installing) watchInstalling(installing);
    };

    container.addEventListener('controllerchange', handleControllerChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const timer = window.setInterval(requestUpdateCheck, UPDATE_CHECK_INTERVAL_MS);

    void container
      .register(SERVICE_WORKER_URL, { scope: '/' })
      .then((registration) => {
        if (disposed) return;
        registrationRef.current = registration;
        trackedRegistration = registration;
        // 既に待機中の新 SW がいる（前回のセッションで検知済み）ケース
        if (registration.waiting && container.controller) markUpdateReady();
        // register() の解決前に updatefound が発火していると handleUpdateFound は
        // 呼ばれない（別タブが先にインストールを始めた場合など）。取りこぼさないよう
        // 現在 installing 中のワーカーもここで拾う。
        if (registration.installing) watchInstalling(registration.installing);
        registration.addEventListener('updatefound', handleUpdateFound);
      })
      .catch((error: unknown) => {
        // 登録失敗はアプリ本体に影響させない（オフライン対応と更新通知が無効になるだけ）。
        console.warn('[useAppUpdate] Service Worker の登録に失敗しました。', error);
      });

    return () => {
      disposed = true;
      container.removeEventListener('controllerchange', handleControllerChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      trackedRegistration?.removeEventListener('updatefound', handleUpdateFound);
      window.clearInterval(timer);
    };
  }, [enabled]);

  const reloadOnce = useCallback((): void => {
    if (reloadingRef.current) return;
    reloadingRef.current = true;
    window.location.reload();
  }, []);

  const applyUpdate = useCallback((): void => {
    updateAcceptedRef.current = true;
    const waiting = registrationRef.current?.waiting;
    if (!waiting) {
      // waiting が取れない異常時は素直にリロードする（更新を諦めさせない）。
      reloadOnce();
      return;
    }

    // 新 SW を確定 → activate → controllerchange → リロード
    waiting.postMessage({ type: 'SKIP_WAITING' });

    // controllerchange が来ないまま無反応なボタンにしない。
    // 既に controllerchange 経由でリロード済みなら reloadingRef が立っているので何もしない。
    window.setTimeout(reloadOnce, SKIP_WAITING_TIMEOUT_MS);
  }, [reloadOnce]);

  const dismissUpdate = useCallback((): void => {
    setIsUpdateReady(false);
  }, []);

  return { isUpdateReady, applyUpdate, dismissUpdate };
};
